import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { Modal } from '../../components/Modal';
import { useAuth } from '../auth/AuthContext';

/**
 * 1:1 counselling booking — openCounsel() (:5167) + bookCounsel() (:5171),
 * markup from :5315.
 *
 * Doubles as the centre free-class booking: bookFreeClass (:5209) does not
 * book anything itself, it presets goal="Free trial class" and
 * slot="Weekend morning" and opens this same modal. `preset.centre*` carries
 * the centre through, which the prototype drops — see the note on the endpoint.
 */

export const COUNSEL_GOALS = [
  'Choosing the right course',
  'Career change advice',
  'Free trial class',
  'Corporate / team training',
  'Something else',
];

export const COUNSEL_SLOTS = [
  'Weekday morning',
  'Weekday afternoon',
  'Weekday evening',
  'Weekend morning',
  'Weekend evening',
];

export interface CounselPreset {
  goal?: string;
  slot?: string;
  centreSlug?: string;
  centreName?: string;
}

export function CounselModal({
  preset,
  onClose,
  onBooked,
}: {
  preset?: CounselPreset;
  onClose: () => void;
  onBooked: (msg: string) => void;
}) {
  const { user } = useAuth();
  // openCounsel (:5167) pre-fills name/email for a logged-in user.
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(user?.email ?? '');
  const [goal, setGoal] = useState(preset?.goal ?? COUNSEL_GOALS[0]!);
  const [slot, setSlot] = useState(preset?.slot ?? COUNSEL_SLOTS[0]!);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const forCentre = !!preset?.centreSlug;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // bookCounsel (:5176) — "Name and phone are required"
    if (!name.trim() || !phone.trim()) {
      setError('Name and phone are required');
      return;
    }
    setBusy(true);
    try {
      if (forCentre) {
        await api.post(`/centres/${preset!.centreSlug}/book`, { name, phone, email: email || undefined, slot });
        onBooked(`Free class booked at ${preset!.centreName} — our advisor will call you 📞`);
      } else {
        await api.post('/counselling', { name, phone, email: email || undefined, goal, slot });
        onBooked('Booked — our advisor will call you 📞');
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} label="Book a free 1:1 session">
      <h2 style={{ fontSize: 19 }}>
        {forCentre ? `Book a free class at ${preset!.centreName}` : 'Book a free 1:1 session'}
      </h2>
      <p className="msub">
        {forCentre
          ? 'Tell us how to reach you and an advisor will confirm your seat.'
          : 'Talk to an advisor about which course fits your goal. Free, no obligation.'}
      </p>

      <form onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label htmlFor="cnName">Your name</label>
          <input id="cnName" data-testid="cn-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cnPhone">Phone</label>
          <input id="cnPhone" data-testid="cn-phone" placeholder="01X-XXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cnEmail">Email (optional)</label>
          <input
            id="cnEmail"
            data-testid="cn-email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* A centre booking's goal IS the free trial class — asking again would
            let the two disagree. */}
        {!forCentre ? (
          <div className="field">
            <label htmlFor="cnGoal">What do you want help with?</label>
            <select id="cnGoal" data-testid="cn-goal" value={goal} onChange={(e) => setGoal(e.target.value)}>
              {COUNSEL_GOALS.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="cnSlot">Preferred time</label>
          <select id="cnSlot" data-testid="cn-slot" value={slot} onChange={(e) => setSlot(e.target.value)}>
            {COUNSEL_SLOTS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>

        {error ? (
          <p data-testid="cn-error" style={{ color: 'var(--coral)', fontSize: 13.5 }}>
            {error}
          </p>
        ) : null}

        <button className="btn btn-primary btn-block" type="submit" data-testid="cn-submit" disabled={busy}>
          {busy ? 'Booking…' : forCentre ? 'Book my free class' : 'Book my session'}
        </button>
      </form>
    </Modal>
  );
}
